import './app.scss'

import { lazy, Suspense, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Route, Routes } from 'react-router-dom'
import Layout from 'src/widgets/--deprecated/layout/layout'
import PrivateRoute from 'src/widgets/--deprecated/private-route/private-route'
import ScrollToTop from 'src/widgets/--deprecated/scroll-to-top/scroll-to-top'

import { SPID_ENABLED } from './config'
import Accessibilita from './containers/accessibilita/accessibilita'
import AssistanceDetail from './containers/assistance-detail/assistance-detail'
import SpidLogin from './containers/authenticate-spid/spid-login'
import Community from './containers/community/community'
import CookiePolicy from './containers/cookie-policy/cookie-policy'
import Faq from './containers/faq/faq'
import Impersonification from './containers/impersonification/impersonification'
import Maintenance from './containers/maintenance/maintenance'
import ModifyResidence from './containers/modify-residence/modify-residence'
import NewPassword from './containers/new-password/new-password'
import NotSupportedBrowser from './containers/not-supported-browser/not-supported-browser'
import PasswordExpired from './containers/password-expired/password-expired'
import PasswordReset from './containers/password-reset/password-reset'
import PaymentOutcome from './containers/policy-operations/renewal/payment-outcome'
import PublicAuthorityPolicyDetail from './containers/public-authority-policy-detail/public-authority-policy-detail'
import PublicAuthority from './containers/public-authority/public-authority'
import RecoveryCredentials from './containers/recovery-credentials/recovery-credentials'
import Servicies from './containers/services/servicies'
import SetupPassword from './containers/setup-password/setup-password'
import UnlockUser from './containers/unlock-user/unlock-user'
import VoucherDetail from './containers/voucher-detail/voucher-detail'
import i18n from './i18n'
import {
  NAV_TO_ACCESSIBILITA,
  NAV_TO_AGENCIES,
  NAV_TO_AGENCY_DETAIL,
  NAV_TO_ASSISTANCE,
  NAV_TO_ASSISTANCE_DETAIL,
  NAV_TO_CHANGE_RESIDENCE,
  NAV_TO_CLAIM,
  NAV_TO_COMMUNITY,
  NAV_TO_DEPOSIT,
  NAV_TO_DOCUMENTS,
  NAV_TO_FAQS,
  NAV_TO_HOMEPAGE,
  NAV_TO_LOGIN,
  NAV_TO_MAINTENANCE,
  NAV_TO_NEW_PASSWORD,
  NAV_TO_NOT_COOKIE_POLICY,
  NAV_TO_NOT_SUPPORTED_BROWSER,
  NAV_TO_ON_BOARDING,
  NAV_TO_ON_IMPERSONIFICATION,
  NAV_TO_ON_PAYMENT_OUTCOME,
  NAV_TO_ON_REGISTRATION_CONFIRMATION,
  NAV_TO_PASSWORD_EXPIRED,
  NAV_TO_PASSWORD_RESET,
  NAV_TO_POLICIES,
  NAV_TO_POLICY_DETAIL,
  NAV_TO_PROFILE,
  NAV_TO_PUBLIC_AUTHORITY,
  NAV_TO_PUBLIC_AUTHORITY_POLICY_DETAIL,
  NAV_TO_RECOVERY_CREDENTIALS,
  NAV_TO_REDEMPTION,
  NAV_TO_REGISTRATION,
  NAV_TO_RENEWAL,
  NAV_TO_REQUESTS_HISTORY,
  NAV_TO_SERVICIES,
  NAV_TO_SETUP_PASSWORD,
  NAV_TO_SPID_LOGIN,
  NAV_TO_UNLOCK_USER,
  NAV_TO_VOUCHER_DETAIL,
} from './navigation'
import { shoot } from './redux/actions'
import { select_maintenance_active } from './redux/store'

export interface react_native_window extends Window {
  ReactNativeWebView?: {
    postMessage?: (message: string) => void,
  };
}

window.onbeforeunload = () => window.scrollTo(0, 0)

const Login = lazy(() => import('./containers/login/login'))
const Assistance = lazy(() => import('./containers/assistance/assistance'))
const QuickRegistration = lazy(() => import('./containers/registration/quick-registration'))
const Body = lazy(() => import('./containers/homepage/homepage'))
const Onboarding = lazy(() => import('./containers/onboarding/onboarding'))
const ReCo = lazy(() => import('./containers/registration-confirmation/registration-confirmation'))
const Profile = lazy(() => import('./containers/profile/profile'))
const Policies = lazy(() => import('./containers/policies/policies'))
const PolicyDetail = lazy(() => import('./containers/policy-detail/policy-detail'))
const Claim = lazy(() => import('./containers/policy-operations/claim/claim'))
const Deposit = lazy(() => import('./containers/policy-operations/deposit/deposit'))
const Redemption = lazy(() => import('./containers/policy-operations/redemption/redemption'))
const Renewal = lazy(() => import('./containers/policy-operations/renewal/renewal'))
const Documents = lazy(() => import('./containers/documents/documents'))
const RequestsHistory = lazy(() => import('./containers/requests-history/requests-history'))
const Agencies = lazy(() => import('./containers/agencies/agencies'))
const AgencyDetail = lazy(() => import('./containers/agency-detail/agency-detail'))

const App: React.FC = () => {
  const dispatch = useDispatch()

  const maintenance_active = useSelector(select_maintenance_active)

  useEffect(() => {
    dispatch(shoot.saga_resolve_communications_registration({ domain: 'MNT', subdomain: 'ASM' }))
  }, [])

  window.addEventListener('message', (message) => {
    const data = message?.data || ''
    if (data.startsWith('LANG:')) {
      const curr_language = i18n.language.slice(0, 2).toLowerCase()
      const next_language = data.split(':')[1] || 'it'
      if (curr_language !== next_language) {
        i18n.changeLanguage(next_language)
        dispatch(shoot.saga_invalidate_cache())
        setTimeout(() => window.location.reload())
      }
    }
  })

  window.addEventListener('message', (message) => {
    const data = message?.data || ''
    if (data.startsWith('FROM_APP')) {
      // TODO: store from_app --> true
    }
  })

  const routes = (
    <Routes>
      <Route path={NAV_TO_HOMEPAGE} element={<PrivateRoute component={Body} />} />
      <Route path={NAV_TO_PUBLIC_AUTHORITY} element={<PublicAuthority />} />
      <Route
        path={NAV_TO_PUBLIC_AUTHORITY_POLICY_DETAIL}
        element={<PublicAuthorityPolicyDetail />}
      />
      <Route path={NAV_TO_ASSISTANCE} element={<PrivateRoute component={Assistance} />} />
      <Route path={NAV_TO_ON_BOARDING} element={<PrivateRoute component={Onboarding} />} />
      <Route path={NAV_TO_PROFILE} element={<PrivateRoute component={Profile} />} />
      <Route
        path={NAV_TO_CHANGE_RESIDENCE}
        element={<PrivateRoute component={ModifyResidence} />}
      />
      <Route path={NAV_TO_POLICIES} element={<PrivateRoute component={Policies} />} />
      <Route path={NAV_TO_POLICY_DETAIL} element={<PrivateRoute component={PolicyDetail} />} />
      <Route path={NAV_TO_CLAIM} element={<PrivateRoute component={Claim} />} />
      <Route path={NAV_TO_DEPOSIT} element={<PrivateRoute component={Deposit} />} />
      <Route path={NAV_TO_REDEMPTION} element={<PrivateRoute component={Redemption} />} />
      <Route path={NAV_TO_DOCUMENTS} element={<PrivateRoute component={Documents} />} />
      <Route
        path={NAV_TO_REQUESTS_HISTORY}
        element={<PrivateRoute component={RequestsHistory} />}
      />
      <Route path={NAV_TO_RENEWAL} element={<PrivateRoute component={Renewal} />} />
      <Route path={NAV_TO_PASSWORD_RESET} element={<PrivateRoute component={PasswordReset} />} />
      <Route path={NAV_TO_AGENCIES} element={<PrivateRoute component={Agencies} />} />
      <Route path={NAV_TO_AGENCY_DETAIL} element={<PrivateRoute component={AgencyDetail} />} />
      <Route path={NAV_TO_COMMUNITY} element={<PrivateRoute component={Community} />} />
      <Route path={NAV_TO_VOUCHER_DETAIL} element={<PrivateRoute component={VoucherDetail} />} />
      <Route
        path={NAV_TO_ASSISTANCE_DETAIL}
        element={<PrivateRoute component={AssistanceDetail} />}
      />

      {(SPID_ENABLED === 'true' && <Route path={NAV_TO_SPID_LOGIN} element={<SpidLogin />} />)}

      <Route path={NAV_TO_SERVICIES} element={<Servicies />} />
      <Route path={NAV_TO_ON_PAYMENT_OUTCOME} element={<PaymentOutcome />} />
      <Route path={NAV_TO_ON_REGISTRATION_CONFIRMATION} element={<ReCo />} />
      <Route path={NAV_TO_ON_IMPERSONIFICATION} element={<Impersonification />} />
      <Route path={NAV_TO_SETUP_PASSWORD} element={<SetupPassword />} />
      <Route path={NAV_TO_NEW_PASSWORD} element={<NewPassword />} />
      <Route path={NAV_TO_UNLOCK_USER} element={<UnlockUser />} />
      <Route path={NAV_TO_REGISTRATION} element={<QuickRegistration />} />
      <Route path={NAV_TO_LOGIN} element={<Login />} />
      <Route path={NAV_TO_RECOVERY_CREDENTIALS} element={<RecoveryCredentials />} />
      <Route path={NAV_TO_PASSWORD_EXPIRED} element={<PasswordExpired />} />
      <Route path={NAV_TO_FAQS} element={<Faq />} />
      <Route path={NAV_TO_NOT_SUPPORTED_BROWSER} element={<NotSupportedBrowser />} />
      <Route path={NAV_TO_NOT_COOKIE_POLICY} element={<CookiePolicy />} />
      <Route path={NAV_TO_ACCESSIBILITA} element={<Accessibilita />} />
      <Route path="/" element={<Login />} />
    </Routes>
  )

  const maintenance_routes = (
    <Routes>
      <Route path={NAV_TO_MAINTENANCE} element={<Maintenance />} />
      <Route path="/" element={<Maintenance />} />
    </Routes>
  )

  return (
    <Layout>
      <ScrollToTop />
      <Suspense fallback={<div />}>{maintenance_active ? maintenance_routes : routes}</Suspense>
    </Layout>
  )
}

export default App
